import type { Query } from "@cubejs-client/core"
import type { TableVisualizationColumn } from "~/ui-rtk/constants/table-visualization-types"
import type { ConvertNumberType } from "~/ui-rtk/utils/number-utils"
import type { TReportChartConfig } from "~/ui-rtk/pages/BrandMedia/CreativeTaxonomyReport/components/Report"

/** Resolves a measure member (e.g. `cube.spend`) to its display format. */
export type ResolveMeasureFormat = (
  measure: string,
) => ConvertNumberType | undefined

/** `view.measure` -> `measure` (matches Report's row-key stripping). */
export const shortKey = (member: string) => member.replace(/^[^.]+\./, "")

/** `view.measure` -> `view` (the cube/view a member belongs to). */
export const cubeOf = (member: string) => member.split(".")[0]

const humanize = (member: string) =>
  shortKey(member)
    .replace(/_/g, " ")
    .replace(/\b\w/g, c => c.toUpperCase())

/**
 * Build the `columns` + `chart` props for the generic Report component from a
 * stored custom cube query (measures + dimensions). Dimensions become text
 * columns and the chart x-axis; measures become numeric columns and bar series.
 */
export function deriveReportConfig(
  query: Query,
  resolveFormat?: ResolveMeasureFormat,
): {
  columns: TableVisualizationColumn[]
  chart: TReportChartConfig
} {
  const measures = (query.measures ?? []) as string[]
  const dimensions = (query.dimensions ?? []) as string[]

  const dimColumns: TableVisualizationColumn[] = dimensions.map(d => ({
    key: shortKey(d),
    header: humanize(d),
    type: "text",
  }))
  const measureColumns: TableVisualizationColumn[] = measures.map(m => ({
    key: shortKey(m),
    header: humanize(m),
    type: resolveFormat?.(m) ?? "number",
  }))

  const xSource = dimensions[0] ?? measures[0] ?? "x"

  return {
    columns: [...dimColumns, ...measureColumns],
    chart: {
      type: "bar",
      xKey: shortKey(xSource),
      numberType: resolveFormat?.(measures[0]) ?? "number",
      // Carry each measure's format so the viewer can format per-measure values
      // (the summary table renders one row per measure with its own unit).
      series: measures.map(m => {
        const numberType = resolveFormat?.(m)
        return {
          key: shortKey(m),
          label: humanize(m),
          ...(numberType ? { numberType } : {}),
        }
      }),
    },
  }
}
